// Earned Media Intelligence — canonical per-dimension state resolver.
//
// KEEP THIS FILE IDENTICAL IN client AND superadmin.
//
// The microsite value chart renders four intelligence dimensions per talent
// row (Audience Fit, Engagement Quality, Conversion Potential, Amplification
// Priority). Historically a missing score rendered as an empty track with the
// text "Research Priority" in the value slot, which reads as a broken zero
// score and conflates three different facts. This resolver separates them:
//
//   value            → the numeric score (null when unscored; NEVER zero-filled)
//   status           → scored | analysis_pending | insufficient_data | not_applicable
//   confidence       → high | medium | low | null (only when explicitly provided)
//   researchPriority → independent recommendation flag (never implied by a
//                      missing score alone)
//   reason           → human-readable explanation for non-scored states
//
// Scores are never fabricated here: a numeric value is surfaced only when the
// stored intelligence explicitly carries one (the backend derives only the
// amplification score from real follower/engagement signals; the other three
// are pass-through). Missing values never become zero.

export const EARNED_MEDIA_DIMENSION_STATUSES = Object.freeze({
  SCORED: "scored",
  ANALYSIS_PENDING: "analysis_pending",
  INSUFFICIENT_DATA: "insufficient_data",
  NOT_APPLICABLE: "not_applicable",
});

export const EARNED_MEDIA_DIMENSION_STATUS_LABELS = Object.freeze({
  [EARNED_MEDIA_DIMENSION_STATUSES.SCORED]: "Scored",
  [EARNED_MEDIA_DIMENSION_STATUSES.ANALYSIS_PENDING]: "Analysis Pending",
  [EARNED_MEDIA_DIMENSION_STATUSES.INSUFFICIENT_DATA]: "Insufficient Data",
  [EARNED_MEDIA_DIMENSION_STATUSES.NOT_APPLICABLE]: "Not Applicable",
});

export const EARNED_MEDIA_GENERIC_UNSCORED_REASON =
  "Additional audience and behavioral signals are required before this dimension can be reliably scored.";

const NOT_APPLICABLE_REASON =
  "This dimension does not apply to the available intelligence for this profile.";

export const EARNED_MEDIA_DIMENSIONS = Object.freeze([
  {
    key: "audienceFit",
    label: "Audience Fit",
    scoreKeys: ["audienceFitScore", "audience_fit_score"],
    statusKeys: ["audienceFitStatus", "audience_fit_status"],
    confidenceKeys: ["audienceFitConfidence", "audience_fit_confidence"],
    reasonKeys: ["audienceFitReason", "audience_fit_reason"],
    missingSignals: [
      "Audience demographics",
      "Interest and affinity composition",
      "Campaign audience alignment",
    ],
    reasons: {
      [EARNED_MEDIA_DIMENSION_STATUSES.ANALYSIS_PENDING]:
        "Audience analysis is still pending.",
      [EARNED_MEDIA_DIMENSION_STATUSES.INSUFFICIENT_DATA]:
        "Audience demographic and affinity signals are not yet available.",
    },
  },
  {
    key: "engagementQuality",
    label: "Engagement Quality",
    scoreKeys: ["engagementQualityScore", "engagement_quality_score"],
    statusKeys: ["engagementQualityStatus", "engagement_quality_status"],
    confidenceKeys: ["engagementQualityConfidence", "engagement_quality_confidence"],
    reasonKeys: ["engagementQualityReason", "engagement_quality_reason"],
    missingSignals: [
      "Recent post interactions",
      "Engagement consistency",
      "Interaction quality",
    ],
    reasons: {
      [EARNED_MEDIA_DIMENSION_STATUSES.ANALYSIS_PENDING]:
        "Engagement analysis is still pending.",
      [EARNED_MEDIA_DIMENSION_STATUSES.INSUFFICIENT_DATA]:
        "Post-level interaction and consistency signals are insufficient for scoring.",
    },
  },
  {
    key: "conversionPotential",
    label: "Conversion Potential",
    scoreKeys: ["conversionPotentialScore", "conversion_potential_score"],
    statusKeys: ["conversionPotentialStatus", "conversion_potential_status"],
    confidenceKeys: ["conversionPotentialConfidence", "conversion_potential_confidence"],
    reasonKeys: ["conversionPotentialReason", "conversion_potential_reason"],
    missingSignals: [
      "Intent and link behavior",
      "CTA response",
      "Downstream campaign response",
    ],
    reasons: {
      [EARNED_MEDIA_DIMENSION_STATUSES.ANALYSIS_PENDING]:
        "Conversion analysis is still pending.",
      [EARNED_MEDIA_DIMENSION_STATUSES.INSUFFICIENT_DATA]:
        "No reliable intent or downstream conversion signals are available.",
    },
  },
  {
    key: "amplificationPriority",
    label: "Amplification Priority",
    scoreKeys: ["amplificationPriorityScore", "amplification_priority_score"],
    statusKeys: ["amplificationPriorityStatus", "amplification_priority_status"],
    confidenceKeys: [
      "amplificationPriorityConfidence",
      "amplification_priority_confidence",
    ],
    reasonKeys: ["amplificationPriorityReason", "amplification_priority_reason"],
    missingSignals: ["Verified reach", "Audience fit", "Engagement quality"],
    reasons: {
      [EARNED_MEDIA_DIMENSION_STATUSES.ANALYSIS_PENDING]:
        "Amplification analysis is still pending.",
      [EARNED_MEDIA_DIMENSION_STATUSES.INSUFFICIENT_DATA]:
        "Verified reach, audience fit, and engagement signals are insufficient for scoring.",
    },
  },
]);

// Strict finite score resolver. Accepts a number or a purely numeric string.
// Text labels ("Research Priority"), empty strings, null, and undefined are
// never scores and never become zero. Ratios (0..1] scale to 0-100 to match
// the established earned-media score domain; results clamp to 0-100.
export const parseEarnedMediaDimensionScore = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value !== "number") {
    const text = String(value).trim();
    if (!text || !/^-?\d+(?:\.\d+)?$/.test(text)) return null;
    value = Number(text);
  }
  if (!Number.isFinite(value)) return null;
  if (value > 0 && value <= 1) return Math.max(0, Math.min(100, Math.round(value * 100)));
  return Math.max(0, Math.min(100, Math.round(value)));
};

export const normalizeEarnedMediaDimensionConfidence = (value) => {
  const text = String(value ?? "").trim().toLowerCase();
  if (text === "high" || text === "medium" || text === "low") return text;
  return null;
};

const STATUS_ALIASES = Object.freeze({
  scored: EARNED_MEDIA_DIMENSION_STATUSES.SCORED,
  analysis_pending: EARNED_MEDIA_DIMENSION_STATUSES.ANALYSIS_PENDING,
  pending: EARNED_MEDIA_DIMENSION_STATUSES.ANALYSIS_PENDING,
  insufficient_data: EARNED_MEDIA_DIMENSION_STATUSES.INSUFFICIENT_DATA,
  insufficient: EARNED_MEDIA_DIMENSION_STATUSES.INSUFFICIENT_DATA,
  unavailable: EARNED_MEDIA_DIMENSION_STATUSES.INSUFFICIENT_DATA,
  not_applicable: EARNED_MEDIA_DIMENSION_STATUSES.NOT_APPLICABLE,
  na: EARNED_MEDIA_DIMENSION_STATUSES.NOT_APPLICABLE,
  n_a: EARNED_MEDIA_DIMENSION_STATUSES.NOT_APPLICABLE,
});

export const normalizeEarnedMediaDimensionStatus = (value) => {
  const text = String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s/-]+/g, "_");
  return STATUS_ALIASES[text] || null;
};

const firstDefined = (row, keys) => {
  for (const key of keys) {
    if (row && row[key] !== undefined && row[key] !== null && row[key] !== "") {
      return row[key];
    }
  }
  return undefined;
};

const readExplicitDimensionMeta = (row, dimension) => {
  const container =
    (row &&
      typeof row === "object" &&
      (row.dimensionStates || row.dimension_states)) ||
    null;
  const explicit =
    container && typeof container === "object" && !Array.isArray(container)
      ? container[dimension.key]
      : null;
  return explicit && typeof explicit === "object" && !Array.isArray(explicit)
    ? explicit
    : {};
};

const normalizeReasonText = (value) => {
  const text = String(value ?? "").trim();
  return text || null;
};

// Research priority is an independent recommendation. It is TRUE only when the
// intelligence explicitly recommends it: either an explicit boolean/flag on
// the row (or dimension metadata), or the established role-based research
// recommendation at High/Medium level. A missing score alone never sets it.
const resolveResearchPriority = ({ row, explicit, status, researchPriorityLevel }) => {
  if (explicit.researchPriority === true) return true;
  if (explicit.researchPriority === false) return false;
  if (row && row.researchPriority === true) return true;
  if (row && row.researchPriority === false) return false;
  if (status === EARNED_MEDIA_DIMENSION_STATUSES.SCORED) return false;
  if (status === EARNED_MEDIA_DIMENSION_STATUSES.NOT_APPLICABLE) return false;
  const level = String(researchPriorityLevel ?? "").trim().toLowerCase();
  return level === "high" || level === "medium";
};

export const resolveEarnedMediaDimensionState = (
  row = {},
  dimension,
  context = {}
) => {
  const explicit = readExplicitDimensionMeta(row, dimension);
  const explicitStatus =
    normalizeEarnedMediaDimensionStatus(explicit.status) ||
    normalizeEarnedMediaDimensionStatus(firstDefined(row, dimension.statusKeys));
  const value = parseEarnedMediaDimensionScore(
    explicit.score ?? explicit.value ?? firstDefined(row, dimension.scoreKeys)
  );
  const confidence =
    normalizeEarnedMediaDimensionConfidence(explicit.confidence) ||
    normalizeEarnedMediaDimensionConfidence(firstDefined(row, dimension.confidenceKeys));
  const providedReason =
    normalizeReasonText(explicit.reason) ||
    normalizeReasonText(firstDefined(row, dimension.reasonKeys));

  // A valid score always wins over stale missing-data labels or metadata.
  let status;
  if (value !== null) {
    status = EARNED_MEDIA_DIMENSION_STATUSES.SCORED;
  } else if (explicitStatus && explicitStatus !== EARNED_MEDIA_DIMENSION_STATUSES.SCORED) {
    status = explicitStatus;
  } else if (context.analysisPending) {
    status = EARNED_MEDIA_DIMENSION_STATUSES.ANALYSIS_PENDING;
  } else {
    status = EARNED_MEDIA_DIMENSION_STATUSES.INSUFFICIENT_DATA;
  }

  const researchPriority = resolveResearchPriority({
    row,
    explicit,
    status,
    researchPriorityLevel: context.researchPriorityLevel,
  });

  let reason = providedReason;
  if (status !== EARNED_MEDIA_DIMENSION_STATUSES.SCORED && !reason) {
    if (status === EARNED_MEDIA_DIMENSION_STATUSES.NOT_APPLICABLE) {
      reason = NOT_APPLICABLE_REASON;
    } else {
      reason = dimension.reasons[status] || EARNED_MEDIA_GENERIC_UNSCORED_REASON;
    }
  }

  return {
    key: dimension.key,
    label: dimension.label,
    value,
    status,
    statusLabel: EARNED_MEDIA_DIMENSION_STATUS_LABELS[status],
    confidence: status === EARNED_MEDIA_DIMENSION_STATUSES.SCORED ? confidence : null,
    researchPriority,
    reason,
    missingSignals:
      status === EARNED_MEDIA_DIMENSION_STATUSES.SCORED ? [] : dimension.missingSignals,
  };
};

export const resolveEarnedMediaDimensionStates = (row = {}, context = {}) =>
  EARNED_MEDIA_DIMENSIONS.map((dimension) =>
    resolveEarnedMediaDimensionState(row, dimension, context)
  );
